<template>
  <div
    v-if="shouldRender"
    class="category-feature"
    ref="categoryFeature"
    :data-category="advertCode"
  >
    <!-- 分类标题 -->
    <div class="category-title" v-if="categoryName">
      <template v-if="categoryImage">
        <img
          :src="categoryImage"
          :alt="categoryName"
          class="category-image"
          @error="handleImageError"
          @load="handleImageLoad"
        />
      </template>
      <h3 v-else>{{ categoryName }}</h3>
    </div>

    <!-- 内容区域添加左右边距 -->
    <div class="category-content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-placeholder">
        <div class="loading-spinner"></div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-message">
        <p>{{ error }}</p>
        <button @click="retryLoading">重试</button>
      </div>

      <!-- 商品列表 -->
      <div v-else-if="products.length" class="products-grid">
        <div
          v-for="product in products"
          :key="product.ProductNo"
          class="product-item"
          @click="handleProductClick(product)"
        >
          <img :src="getImageUrl(product.ImageUrl)" :alt="product.Title" />
          <div class="product-info">
            <h4>{{ product.Title }}</h4>
            <div class="price-container">
              <span class="price"> <small>¥</small>{{ product.Param1 }} </span>
              <span class="cart-icon">🛒</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 无数据状态 -->
      <div v-else class="no-data">暂无商品数据</div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { getCategoryFeatureList } from "@/api/advertisement";
import {
  CategoryNames,
  CategoryFeatureCodes,
} from "@/api/constants/advertCodes";

export default {
  name: "CategoryFeature",

  props: {
    advertCode: {
      type: String,
      required: true,
      validator(value) {
        // console.log(`[CategoryFeature] 验证广告代码:`, value);
        return !!value;
      },
    },
    categoryImages: {
      type: Object,
      default: () => ({}),
      validator(value) {
        // console.log(`[CategoryFeature] 验证分类图片数据:`, {
        //   hasData: !!value,
        //   keysCount: Object.keys(value).length,
        // });
        return true;
      },
    },
  },

  setup(props) {
    const categoryFeature = ref(null);
    const products = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const isIntersecting = ref(false);
    const hasLoaded = ref(false);

    const categoryName = ref(CategoryNames[props.advertCode]);

    // 处理图片URL
    const getImageUrl = (imageUrlString) => {
      try {
        const imageData = JSON.parse(imageUrlString);
        if (Array.isArray(imageData) && imageData.length > 0) {
          const { ServerUrl, FilePath } = imageData[0];
          return ServerUrl ? `${ServerUrl}${FilePath}` : FilePath;
        }
      } catch (e) {
        console.error("解图片URL失败:", e);
      }
      return ""; // 返回空字符串作为默认值
    };

    // 添加调试日志
    // console.log(`[CategoryFeature] 初始化组件 advertCode:${props.advertCode}`);

    // 监听数据加载状态
    watch(loading, (newVal) => {
      // console.log(`[CategoryFeature ${props.advertCode}] 加载状态:`, newVal);
    });

    // 监听错误状态
    watch(error, (newVal) => {
      if (newVal) {
        console.error(`[CategoryFeature ${props.advertCode}] 错误:`, newVal);
      }
    });

    // 监听产数据变化
    watch(products, (newVal) => {
      // console.log(
      //   `[CategoryFeature ${props.advertCode}] 加载产品数量:`,
      //   newVal.length
      // );
    });

    // 监听交叉观察状态
    watch(isIntersecting, (newVal) => {
      // console.log(`[CategoryFeature ${props.advertCode}] 可见性变化:`, newVal);
    });

    // 加载数据
    const loadData = async () => {
      if (hasLoaded.value || loading.value) {
        // console.log(`[CategoryFeature ${props.advertCode}] 跳过加载:`, {
        //   hasLoaded: hasLoaded.value,
        //   loading: loading.value,
        // });
        return;
      }

      // console.log(`[CategoryFeature ${props.advertCode}] 开始加载数据`);
      loading.value = true;
      error.value = null;

      try {
        const response = await getCategoryFeatureList(props.advertCode);
        // console.log(
        //   `[CategoryFeature ${props.advertCode}] 数据响应:`,
        //   response
        // );

        if (response?.Data) {
          products.value = response.Data;
          hasLoaded.value = true;
          // console.log(`[CategoryFeature ${props.advertCode}] 数据加载成功`);
        }
      } catch (err) {
        error.value = "加载失败，请稍后重试";
        console.error(`[CategoryFeature ${props.advertCode}] 加载失败:`, err);
      } finally {
        loading.value = false;
      }
    };

    // 重试加载
    const retryLoading = () => {
      hasLoaded.value = false;
      loadData();
    };

    // 处理商品点击
    const handleProductClick = (product) => {
      if (product.Url) {
        window.location.href = product.Url;
      }
    };

    // 观察器配置
    const observerOptions = {
      root: null,
      rootMargin: "50px",
      threshold: 0.1,
    };

    // 创建观察器
    let observer = null;

    onMounted(() => {
      // console.log(`[CategoryFeature ${props.advertCode}] 组件挂载`);

      const hasIntersectionObserver = "IntersectionObserver" in window;
      // console.log(
      //   `[CategoryFeature ${props.advertCode}] IntersectionObserver支持:`,
      //   hasIntersectionObserver
      // );

      if (!hasIntersectionObserver) {
        console.warn(
          `[CategoryFeature ${props.advertCode}] 浏览器不支持 IntersectionObserver，使用滚动监听`
        );
        // ... 降级处理代码 ...
      } else {
        observer = new IntersectionObserver(([entry]) => {
          isIntersecting.value = entry.isIntersecting;
          // console.log(
          //   `[CategoryFeature ${props.advertCode}] 交叉观察:`,
          //   entry.isIntersecting
          // );

          if (entry.isIntersecting && !hasLoaded.value) {
            loadData();
          }
        }, observerOptions);

        if (categoryFeature.value) {
          observer.observe(categoryFeature.value);
          // console.log(`[CategoryFeature ${props.advertCode}] 开始观察元素`);
        }
      }
    });

    onUnmounted(() => {
      // console.log(`[CategoryFeature ${props.advertCode}] 组件卸载`);
      if (observer) {
        observer.disconnect();
        // console.log(`[CategoryFeature ${props.advertCode}] 停止观察`);
      }
    });

    // 获取分类图片
    const getCategoryImage = (code) => {
      return props.categoryImages[code] || "";
    };

    // 在 setup 函数中添加
    watch(
      () => props.categoryImages,
      (newImages) => {
        // console.log("[CategoryFeature] 收到新的图片数据:", newImages);
        // console.log("[CategoryFeature] 当前分类:", props.advertCode);
        // console.log("[CategoryFeature] 对应图片:", newImages[props.advertCode]);
      },
      { immediate: true }
    );

    const categoryImage = computed(() => {
      const image = props.categoryImages[props.advertCode];
      // console.log(`[CategoryFeature] ${props.advertCode} 图片匹配���果:`, {
      //   advertCode: props.advertCode,
      //   hasImage: !!image,
      //   imageUrl: image,
      // });
      return image || "";
    });

    // 添加图片处理函数
    const handleImageError = (e) => {
      console.error(`[CategoryFeature] ${props.advertCode} 图片加载失败:`, e);
    };

    const handleImageLoad = () => {
      // console.log(`[CategoryFeature] ${props.advertCode} 图片加载成功`);
    };

    // 监听图片变化
    watch(
      () => props.categoryImages,
      (newImages) => {
        // console.log(`[CategoryFeature] ${props.advertCode} 收到图片数据更新:`, {
        //   advertCode: props.advertCode,
        //   availableCodes: Object.keys(newImages),
        //   matchedImage: newImages[props.advertCode],
        // });
      },
      { deep: true, immediate: true }
    );

    // 添加渲染控制
    const shouldRender = computed(() => {
      const hasImages = Object.keys(props.categoryImages).length > 0;
      const hasMatchingImage = !!props.categoryImages[props.advertCode];

      // console.log(`[CategoryFeature] ${props.advertCode} 渲染检查:`, {
      //   hasImages,
      //   hasMatchingImage,
      //   advertCode: props.advertCode,
      // });

      return true; // 即使没有图片也允许渲染，但会显示文字标题
    });

    return {
      categoryFeature,
      products,
      loading,
      error,
      categoryName,
      getImageUrl,
      retryLoading,
      handleProductClick,
      getCategoryImage,
      categoryImage,
      handleImageError,
      handleImageLoad,
      shouldRender,
    };
  },
};
</script>

<style scoped>
.category-feature {
  margin: 20px 0;
}

.category-content {
  padding: 0 2% 15px 2%;
}

.category-title {
  margin: 0 2% 15px 2%;
  padding: 0;
  width: 96%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-title h3 {
  font-size: 20px;
  font-weight: bold;
  color: #fff;
}

.category-title .category-image {
  width: 100%;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  display: block;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.product-item {
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.product-item img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
}

.product-info {
  padding: 8px 0;
}

.product-info h4 {
  font-size: 14px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  font-weight: normal;
}

.price-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 5px;
}

.price {
  color: #ff4400;
  font-size: 16px;
}

.price small {
  font-size: 12px;
  margin-right: 2px;
}

.cart-icon {
  font-size: 18px;
  color: #666;
}

.loading-placeholder {
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.error-message {
  text-align: center;
  padding: 20px;
}

.error-message button {
  padding: 8px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #999;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
